import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useSession } from '../app/providers/SessionProvider.jsx';
import { reauthenticateParentalAccount } from '../services/tvParentalAuth.js';
import {
  normalizeParentalCode, PARENTAL_CODE_PATTERN, tvParentalService, validateParentalPin,
} from '../services/tvParentalService.js';
import '../styles/account.css';
import '../styles/tv-parental.css';

export function TvParentalPage() {
  const { configured, ready, user } = useSession();
  const [params] = useSearchParams();
  // The query's purpose is deliberately ignored. Only the server can bind the
  // request to setup/reset and to the authenticated LG account.
  const requestedCode = normalizeParentalCode(params.get('code'));
  const [code, setCode] = useState(requestedCode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [request, setRequest] = useState(null);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [complete, setComplete] = useState(false);
  const lease = useRef(null);
  const epoch = useRef(0);
  const submitting = useRef(false);
  const confirmationHeading = useRef(null);

  function releaseLease() {
    lease.current?.dispose();
    lease.current = null;
  }

  function clearApproval() {
    epoch.current += 1;
    releaseLease();
    submitting.current = false;
    setBusy(false);
    setPassword('');
    setPin('');
    setConfirmation('');
    setConfirmed(false);
    setRequest(null);
    setVerifiedEmail('');
  }

  useEffect(() => () => { epoch.current += 1; releaseLease(); }, []);
  useEffect(() => {
    // A browser back/forward-cache restore must not restore approval authority.
    const leavePage = () => clearApproval();
    window.addEventListener('pagehide', leavePage);
    return () => window.removeEventListener('pagehide', leavePage);
  }, []);
  useEffect(() => {
    clearApproval(); setCode(requestedCode); setComplete(false); setError('');
  }, [requestedCode]);
  useEffect(() => { if (user?.email) setEmail((current) => current || user.email); }, [user?.email]);
  useEffect(() => {
    if (!request) return undefined;
    confirmationHeading.current?.focus();
    const timer = setTimeout(() => {
      clearApproval();
      setError('Verification expired. Check the request on your LG TV and sign in again.');
    }, Math.max(0, request.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [request]);

  async function verifyAccount(event) {
    event.preventDefault();
    if (submitting.current || !ready || !configured) return;
    if (!PARENTAL_CODE_PATTERN.test(normalizeParentalCode(code))) {
      setError('Enter the 24-character code shown on your LG TV.'); return;
    }
    const operation = ++epoch.current;
    submitting.current = true; setBusy(true); setError('');
    const enteredPassword = password;
    setPassword('');
    let freshLease;
    try {
      // Never use SessionProvider.session or a refreshed existing JWT here.
      freshLease = await reauthenticateParentalAccount(email, enteredPassword);
      if (operation !== epoch.current) { freshLease.dispose(); return; }
      const info = await tvParentalService.info({ accessToken: freshLease.accessToken, code });
      if (operation !== epoch.current) { freshLease.dispose(); return; }
      releaseLease(); lease.current = freshLease;
      setVerifiedEmail(freshLease.email || email.trim());
      // UX timeout only. The server independently validates signed password
      // AMR recency, challenge ordering, account ownership, and one-time use.
      setRequest({ ...info, expiresAt: Math.min(info.expiresAt, Date.now() + 270000) });
    } catch (err) {
      freshLease?.dispose();
      if (operation === epoch.current) setError(err?.message || 'Unable to verify your account.');
    } finally {
      if (operation === epoch.current) { submitting.current = false; setBusy(false); }
    }
  }

  async function approvePin(event) {
    event.preventDefault();
    if (submitting.current) return;
    try {
      validateParentalPin(pin, confirmation);
      if (!confirmed) throw new Error('Confirm that you approve this specific PIN change.');
      if (!lease.current || !request || request.expiresAt <= Date.now()) {
        clearApproval(); throw new Error('Verification expired. Sign in again before continuing.');
      }
    } catch (err) { setError(err.message); return; }
    const operation = epoch.current;
    submitting.current = true; setBusy(true); setError('');
    try {
      await tvParentalService.complete({
        accessToken: lease.current.accessToken, code, purpose: request.purpose,
        pin, confirmation, confirmed,
      });
      if (operation !== epoch.current) return;
      clearApproval(); setComplete(true);
    } catch (err) {
      if (operation !== epoch.current) return;
      clearApproval(); setError(err?.message || 'The result could not be confirmed. Check your LG TV.');
    }
  }

  function cancelApproval() {
    clearApproval(); setError(''); setComplete(false);
  }

  const resetting = request?.purpose === 'reset';
  return (
    <section className="account-page auth-page" data-tv-parental-platform="lg">
      <div className="account-card auth-card tv-parental-card">
        <span className="eyebrow">WatchVIM LG parental controls</span>
        <h1>Manage your LG parental PIN</h1>
        <p className="account-muted">Only continue if you just requested PIN setup or reset in WatchVIM on your LG TV. Never approve a code sent by someone else.</p>
        <p className="account-muted">This verifies access to your account, not your age. It does not change your subscriptions, rentals, or viewing territories.</p>
        {!configured ? <p className="account-message error" role="alert">Account verification is temporarily unavailable. No PIN changes can be made.</p> : null}
        {error ? <p className="account-message error" role="alert">{error}</p> : null}
        {complete ? (
          <p className="account-message success" role="status">Your LG parental PIN is saved. Return to your LG TV and enter your new PIN when prompted. Keep it private from children.</p>
        ) : !request ? (
          <form className="account-form" onSubmit={verifyAccount} aria-label="Verify WatchVIM account">
            <label className="account-field">
              <span>Code shown on your LG TV</span>
              <input name="parental-code" value={code} maxLength={29} autoComplete="off" spellCheck={false} autoCapitalize="characters" disabled={busy} onChange={(event) => setCode(event.target.value)} required />
            </label>
            <label className="account-field">
              <span>WatchVIM email</span>
              <input name="parental-email" type="email" autoComplete="username" value={email} disabled={busy} onChange={(event) => setEmail(event.target.value)} required />
            </label>
            <label className="account-field">
              <span>Account password</span>
              <input name="parental-password" type="password" autoComplete="current-password" value={password} disabled={busy} onChange={(event) => setPassword(event.target.value)} required />
            </label>
            <p className="account-muted">Sign in again with the same account used on your LG TV, even if you are already signed in on this browser. This verification is kept separate from your browser’s saved sign-in.</p>
            <button className="account-primary" type="submit" disabled={busy || !ready || !configured}>{busy ? 'Verifying account…' : 'Verify account'}</button>
            {busy ? <button className="account-secondary compact" type="button" onClick={cancelApproval}>Cancel</button> : null}
          </form>
        ) : (
          <form className="account-form" onSubmit={approvePin} aria-label="Approve LG parental PIN">
            <h2 ref={confirmationHeading} tabIndex={-1}>{resetting ? 'Reset your LG parental PIN?' : 'Set up your LG parental PIN?'}</h2>
            <p className="account-muted">Verified account: <strong>{verifiedEmail}</strong>. LG TV code: <strong>{normalizeParentalCode(code)}</strong>.</p>
            <p className="account-muted">{resetting ? 'This replaces the existing parental PIN and locks restricted playback again. Anyone with the old PIN will need the new one.' : 'Choose a private six-digit PIN to control access to restricted content on your LG TV.'}</p>
            <label className="account-field">
              <span>New six-digit PIN</span>
              <input name="parental-pin" type="password" inputMode="numeric" pattern="[0-9]{6}" minLength={6} maxLength={6} autoComplete="off" value={pin} disabled={busy} onChange={(event) => { setPin(event.target.value); setConfirmed(false); }} required />
            </label>
            <label className="account-field">
              <span>Confirm new PIN</span>
              <input name="parental-pin-confirmation" type="password" inputMode="numeric" pattern="[0-9]{6}" minLength={6} maxLength={6} autoComplete="off" value={confirmation} disabled={busy} onChange={(event) => { setConfirmation(event.target.value); setConfirmed(false); }} required />
            </label>
            <label className="tv-parental-confirmation">
              <input name="parental-approval" type="checkbox" checked={confirmed} disabled={busy} onChange={(event) => setConfirmed(event.target.checked)} required />
              <span>{resetting ? 'I approve replacing my LG parental PIN with this new PIN.' : 'I approve setting up this parental PIN for my LG TV.'}</span>
            </label>
            <button className="account-primary" type="submit" disabled={busy || !confirmed}>{busy ? 'Saving PIN…' : resetting ? 'Approve PIN reset' : 'Approve PIN setup'}</button>
            <button className="account-secondary compact" type="button" disabled={busy} onClick={cancelApproval}>Cancel without changing PIN</button>
          </form>
        )}
        <div className="account-links">
          <NavLink to="/forgot-password">Forgot account password?</NavLink>
          <NavLink to="/support">Get help</NavLink>
        </div>
        <p className="account-muted">TV requests and account verification expire quickly. If either expires, begin again on your LG TV. This page cannot remove content restrictions or bypass account verification.</p>
      </div>
    </section>
  );
}
